import { getAnalysisContentAPI, getAnalysisContentListAPI } from '@/api/AnalysisApi';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { DEV_MODE } from '@/config/dev-mode';
import {
  MONTHLY_FORTUNE_CARD_TYPE_ID,
  MONTHLY_FORTUNE_PARENT_TYPE_ID,
} from '@/components/saju-insight/monthly-fortune/tocCards';

/**
 * 상세분석 서비스 설정 맵
 *
 * 서비스 추가 시 이 맵에 항목을 추가하면 됨.
 * - typeId: API에서 사용하는 analysisTypeId
 * - overviewPath: overview(표지) 메뉴 경로
 * - detailPath: 본문(상세) 메뉴 경로
 * - isPaid: 유료 서비스 여부
 * - freePageLimit: 무료 페이지 수 (유료 서비스만)
 * - indexKey: analysisStore의 서비스별 인덱스 ref 이름 (유료 서비스만)
 * - useAccess: 권한 판단을 /analysis/access 의 canAccess 로 할지 여부.
 *     단순 권한(이용내역 1건=권한) 서비스에만 적용. 월 단위(월운)·카테고리별(궁합)처럼
 *     별도 권한 로직이 있는 서비스는 false 로 두고 기존 logData 기반 판단을 유지한다.
 */
export const ANALYSIS_SERVICE_CONFIG = {
  sajuSummary: {
    typeId: 8,
    overviewPath: '/overviewSajuSummary',
    detailPath: '/summary',
    isPaid: false,
  },
  personality: {
    typeId: 3,
    overviewPath: '/overviewTendency',
    detailPath: '/personality',
    freePageLimit: 3,
    indexKey: 'personalityIndex',
    isPaid: true,
    useAccess: true,
  },
  yongsin: {
    typeId: 4,
    overviewPath: '/overviewYongsin',
    detailPath: '/youngsin',
    freePageLimit: 2,
    indexKey: 'yongsinIndex',
    isPaid: true,
    useAccess: true,
  },
  yearlyFortune: {
    typeId: 9,
    overviewPath: '/overviewYearlyFortune',
    detailPath: '/yearly-fortune',
    freePageLimit: 4,
    indexKey: 'yearlyFortuneIndex',
    isPaid: true,
    useAccess: true,
  },
  monthlyFortune: {
    typeId: 10,
    overviewPath: '/overviewMonthlyFortune',
    detailPath: '/monthly-fortune',
    freePageLimit: 2,
    indexKey: 'monthlyFortuneIndex',
    isPaid: true,
  },
  nangmanHarmony: {
    typeId: 38,
    overviewPath: '/overviewNangmanHarmony',
    detailPath: '/nangman-harmony',
    freePageLimit: 0,
    indexKey: 'nangmanHarmonyIndex',
    isPaid: true,
  },
  yongsinLight: {
    typeId: 41,
    overviewPath: '/overviewYsLight',
    detailPath: '/ys-light',
    freePageLimit: 1,
    indexKey: 'yongsinLightIndex',
    isPaid: true,
    useAccess: true,
  },
  nangmanNaming: {
    typeId: 47,
    overviewPath: '/overviewNangmanNaming',
    detailPath: '/saju-naming',
    freePageLimit: 0,
    indexKey: 'nangmanNamingIndex',
    isPaid: true,
  },
};

/** menu prefix → { key, cfg } 매핑 배열 (내부 유틸) */
const MENU_PATH_ENTRIES = Object.entries(ANALYSIS_SERVICE_CONFIG).flatMap(([key, cfg]) => [
  { prefix: cfg.overviewPath, key, cfg },
  { prefix: cfg.detailPath, key, cfg },
]);

/**
 * 모든 상세분석 서비스의 데이터를 통합 관리하는 Composable
 *
 * - 서비스별 content/logData/permission/ticketData/ticketCount 자동 생성
 * - selectedMenu 기반으로 현재 활성 서비스 데이터 자동 파생
 * - 서비스 추가 시 ANALYSIS_SERVICE_CONFIG에만 항목 추가하면 됨
 */
export const useAnalysisServices = () => {
  const store = useAnalysisStore();
  const paymentsStore = usePaymentsStore();

  const { selectedMenu, analysisLogByType, analysisLogData, analysisAccessByType } =
    storeToRefs(store);
  const ticketsData = computed(() => paymentsStore?.analysisTicketsData || null);

  // ─── 서비스별 데이터 자동 생성 ─────────────────────────────────────────────

  /** 서비스별 content ref */
  const serviceContents = Object.fromEntries(
    Object.keys(ANALYSIS_SERVICE_CONFIG).map((key) => [key, ref(null)]),
  );

  /**
   * 낭만월운 자식 카드별 콘텐츠 (parentId=10 children)
   * - typeId(42~46) → content 매핑
   * - 카드 가격(pointPrice)은 여기서 조회
   */
  const monthlyFortuneChildContents = ref({});

  /** 서비스별 파생 computed */
  const serviceData = Object.fromEntries(
    Object.entries(ANALYSIS_SERVICE_CONFIG).map(([key, cfg]) => {
      /** 해당 서비스의 접근 자격 item (/analysis/access). 없으면 null */
      const accessItem = computed(() =>
        cfg.useAccess ? (analysisAccessByType.value[cfg.typeId] || null) : null,
      );
      // useAccess 타입은 access 응답으로 이용내역(표지 날짜)을 충당 → 타입별 /analysis/log 호출 제거.
      // - 분석권 기반: analysisLogs(요약) 그대로 사용. 단 요약 DTO엔 createdAt(시작일)이 없어 '-' 처리.
      // - 구독 전용 접근(analysisLogs 빈 배열): 구독 기간으로 날짜 합성 → 시작일=accessStartDate,
      //   만료일/D-Day=accessEndDate (§3.2 구독 종료일 반영).
      // - access 미로드 시에는 기존 log 캐시로 폴백.
      const logData = computed(() => {
        if (cfg.useAccess && accessItem.value) {
          const ai = accessItem.value;
          if (ai.analysisLogs?.length) return ai.analysisLogs;
          if (ai.canAccess && ai.subscription) {
            return [
              {
                createdAt: ai.subscription.accessStartDate ?? null,
                expiryDate: ai.subscription.accessEndDate ?? null,
              },
            ];
          }
        }
        return (
          analysisLogByType.value[cfg.typeId] ||
          analysisLogData.value?.filter((d) => d.analysisTypeId === cfg.typeId) ||
          []
        );
      });
      // 무료 서비스는 항상 permission = true.
      // useAccess 서비스는 canAccess(분석권+구독 통합) 기준, access 미로드 시 logData 폴백.
      const permission = cfg.isPaid
        ? computed(() => {
            if (cfg.useAccess && accessItem.value) {
              return accessItem.value.canAccess === true;
            }
            return logData.value?.length > 0;
          })
        : computed(() => true);
      const ticketData = computed(() =>
        cfg.isPaid ? (ticketsData.value?.find((t) => t.typeId === cfg.typeId) || null) : null,
      );
      const ticketCount = computed(() => (cfg.isPaid ? (ticketData.value?.totalCount || 0) : 0));

      return [key, { logData, accessItem, permission, ticketData, ticketCount }];
    }),
  );

  // ─── 메뉴 → 서비스 매핑 유틸 ────────────────────────────────────────────

  /**
   * menu 문자열에서 서비스 key를 반환
   * @returns {string|null} ANALYSIS_SERVICE_CONFIG의 key 또는 null
   */
  const findServiceKeyByMenu = (menu) => {
    if (!menu) return null;
    for (const { prefix, key } of MENU_PATH_ENTRIES) {
      if (menu.startsWith(prefix)) return key;
    }
    return null;
  };

  /**
   * menu 문자열에서 analysisTypeId를 반환
   */
  const getTypeIdByMenu = (menu) => {
    const key = findServiceKeyByMenu(menu);
    return key ? ANALYSIS_SERVICE_CONFIG[key].typeId : null;
  };

  // ─── 현재 활성 서비스 (selectedMenu 기반 자동 파생) ────────────────────────

  /** 현재 활성 서비스 key */
  const activeServiceKey = computed(() => findServiceKeyByMenu(selectedMenu.value));

  /** 현재 활성 서비스의 content */
  const activeContent = computed(() => {
    const key = activeServiceKey.value;
    return key ? (serviceContents[key]?.value ?? null) : null;
  });

  /** 현재 활성 서비스의 ticketData */
  const activeTicketData = computed(() => {
    const key = activeServiceKey.value;
    return key ? (serviceData[key]?.ticketData.value ?? null) : null;
  });

  /** 현재 활성 서비스의 ticketCount */
  const activeTicketCount = computed(() => {
    const key = activeServiceKey.value;
    return key ? (serviceData[key]?.ticketCount.value ?? 0) : 0;
  });

  // ─── 무료 페이지 범위 체크 ─────────────────────────────────────────────────

  /**
   * 현재 메뉴가 본문(detail)이면서 무료 페이지 범위 내인지 여부 (유료 서비스만 해당)
   * true → 결제정보 패널 표시, false → 상세 패널 표시
   */
  const isWithinFreePageLimit = computed(() => {
    const key = activeServiceKey.value;
    if (!key) return false;

    const cfg = ANALYSIS_SERVICE_CONFIG[key];
    if (!cfg.isPaid) return false;

    const menu = selectedMenu.value;

    // overview 경로는 해당 없음
    if (menu.startsWith(cfg.overviewPath)) return false;

    // detail 경로만 처리
    if (menu.startsWith(cfg.detailPath)) {
      const currentIndex = store[cfg.indexKey];
      const limit = cfg.freePageLimit;
      const permission = serviceData[key]?.permission.value;

      if (currentIndex < limit) return true;
      if (currentIndex === limit && !permission) return true;
    }

    return false;
  });

  // ─── API: 유료 서비스 content 병렬 fetch ──────────────────────────────────

  /**
   * 유료 서비스의 content(가격정보)를 병렬로 조회
   */
  const fetchAllContents = async () => {
    const paidEntries = Object.entries(ANALYSIS_SERVICE_CONFIG).filter(([, cfg]) => cfg.isPaid);
    // allSettled 사용: 특정 분석 타입(예: 운영 미등록 상태인 낭만작명 typeId 47)이
    // 400 등으로 실패하더라도, 나머지 서비스의 가격정보는 정상 채워 결제가 막히지 않도록 한다.
    const results = await Promise.allSettled(
      paidEntries.map(([, cfg]) => getAnalysisContentAPI({ analysisTypeId: cfg.typeId })),
    );
    paidEntries.forEach(([key, cfg], idx) => {
      const result = results[idx];
      if (result.status === 'fulfilled') {
        serviceContents[key].value = result.value;
      } else {
        DEV_MODE &&
          console.error(`fetchAllContents(${key}, typeId=${cfg.typeId}) 실패:`, result.reason);
      }
    });

    DEV_MODE && console.log('=== fetchAllContents 완료 ===');
    DEV_MODE &&
      paidEntries.forEach(([key], idx) => {
        const result = results[idx];
        if (result.status === 'fulfilled') {
          console.log(`${ANALYSIS_SERVICE_CONFIG[key].typeId} (${key}):`, result.value?.pointPrice);
        }
      });
  };

  /**
   * 낭만월운 자식 카드 가격 일괄 조회 (parentId=10)
   * 한 번의 호출로 자식 5개의 가격을 받아 typeId로 색인.
   */
  const fetchMonthlyFortuneChildContents = async () => {
    try {
      const response = await getAnalysisContentListAPI({
        parentId: MONTHLY_FORTUNE_PARENT_TYPE_ID,
        isActive: true,
        page: 1,
        size: -1,
      });
      const list = response?.content || [];
      const byType = {};
      list.forEach((item) => {
        byType[item.analysisTypeId] = item;
      });
      monthlyFortuneChildContents.value = byType;
    } catch (e) {
      DEV_MODE && console.error('fetchMonthlyFortuneChildContents 실패:', e);
    }
  };

  /**
   * 카드 key 에 해당하는 자식 콘텐츠(가격 포함) 조회
   * @param {string} cardKey - tocCards 의 카드 key (capability/mode/money/romanceStyle/caution)
   */
  const getMonthlyFortuneCardContent = (cardKey) => {
    const typeId = MONTHLY_FORTUNE_CARD_TYPE_ID[cardKey];
    if (!typeId) return null;
    return monthlyFortuneChildContents.value[typeId] || null;
  };

  /**
   * 카드 key 의 가격
   */
  const getMonthlyFortuneCardPrice = (cardKey) => {
    return getMonthlyFortuneCardContent(cardKey)?.pointPrice ?? null;
  };

  /**
   * 특정 서비스의 content(가격정보)를 단건 조회
   * @param {string} key - ANALYSIS_SERVICE_CONFIG의 서비스 key
   */
  const fetchContentByKey = async (key) => {
    const cfg = ANALYSIS_SERVICE_CONFIG[key];
    if (!cfg?.isPaid) return;
    try {
      serviceContents[key].value = await getAnalysisContentAPI({ analysisTypeId: cfg.typeId });
      DEV_MODE && console.log(`fetchContentByKey(${key}):`, serviceContents[key].value?.pointPrice);
    } catch (e) {
      DEV_MODE && console.error(`fetchContentByKey(${key}) 실패:`, e);
    }
  };

  // ─── API: 접근 자격(canAccess) 일괄 조회 ──────────────────────────────────

  /** useAccess 가 켜진 서비스들의 typeId 목록 */
  const ACCESS_TYPE_IDS = Object.values(ANALYSIS_SERVICE_CONFIG)
    .filter((cfg) => cfg.useAccess)
    .map((cfg) => cfg.typeId);

  /**
   * useAccess 서비스들의 접근 자격을 /analysis/access 1회 호출로 일괄 조회.
   * 타입별 /analysis/log 반복 호출 대신 권한 상태를 한 번에 구성한다.
   * @param {number} guestUserId - 저장사주 ID
   */
  const fetchAllAccess = async (guestUserId) => {
    if (!guestUserId) return;
    await store.fetchAnalysisAccess(guestUserId, ACCESS_TYPE_IDS);
  };

  // ─── permission 체크 ──────────────────────────────────────────────────────

  /**
   * 특정 typeId의 permission 확인
   * @returns {boolean}
   */
  const hasPermissionByTypeId = (typeId) => {
    const entry = Object.entries(ANALYSIS_SERVICE_CONFIG).find(([, cfg]) => cfg.typeId === typeId);
    if (!entry) return false;
    return serviceData[entry[0]]?.permission.value || false;
  };

  return {
    serviceContents,
    serviceData,
    activeServiceKey,
    activeContent,
    activeTicketData,
    activeTicketCount,
    fetchAllContents,
    fetchContentByKey,
    fetchAllAccess,
    ACCESS_TYPE_IDS,
    getTypeIdByMenu,
    isWithinFreePageLimit,
    hasPermissionByTypeId,
    // 낭만월운 카드별 가격/콘텐츠
    monthlyFortuneChildContents,
    fetchMonthlyFortuneChildContents,
    getMonthlyFortuneCardContent,
    getMonthlyFortuneCardPrice,
  };
};
